/**
 * Redteam Config Utilities
 * Helper functions to build and manage redteam configuration
 */

import type {
  RedteamConfig,
  ProviderOptions,
  AgentType,
  FoundationModelId,
  HttpMethod,
  HttpHeader,
  RedteamPlugin,
  PluginConfig,
  RedteamStrategy,
} from "@/types/redteam";
import { REDTEAM_DEFAULTS } from "@/constants/redteam";
import { convertToUnifiedConfig } from "./yamlGenerator";

/**
 * Creates an HTTP provider options object for "own agent" configuration
 */
export function createHttpProviderOptions(
  url: string,
  method: HttpMethod,
  headers: HttpHeader[],
  body: string
): ProviderOptions {
  // Filter out empty headers and convert to object
  const headersObj: Record<string, string> = {};
  headers
    .filter((h) => h.name.trim() && h.value.trim())
    .forEach((h) => {
      headersObj[h.name.trim()] = h.value.trim();
    });

  // Parse body if it's JSON string, otherwise use as-is
  let parsedBody: string | Record<string, unknown> = body.trim();
  try {
    parsedBody = JSON.parse(body);
  } catch {
    // If not valid JSON, use as string
    parsedBody = body.trim();
  }

  return {
    id: "http",
    label: "Custom HTTP Endpoint",
    delay: 500, // 500ms delay between tests to prevent overwhelming the API endpoint
    config: {
      url: url.trim(),
      method,
      headers: headersObj,
      body: parsedBody,
      stateful: true, // Default to stateful for demo flow
    },
  };
}

/**
 * Creates a foundation model provider options object for "our AI" configuration
 */
export function createFoundationModelProviderOptions(
  modelId: FoundationModelId,
  label?: string,
  advancedConfig?: {
    temperature?: number;
    max_tokens?: number;
    top_p?: number;
    apiKey?: string;
  }
): ProviderOptions {
  const config: Record<string, unknown> = {};

  if (advancedConfig?.temperature !== undefined) {
    config.temperature = advancedConfig.temperature;
  }
  if (advancedConfig?.max_tokens !== undefined) {
    config.max_tokens = advancedConfig.max_tokens;
  }
  if (advancedConfig?.top_p !== undefined) {
    config.top_p = advancedConfig.top_p;
  }
  if (advancedConfig?.apiKey) {
    config.apiKey = advancedConfig.apiKey;
  }

  return {
    id: modelId,
    label: label || modelId,
    config,
  };
}

/**
 * Creates a default redteam config object
 */
export function createDefaultRedteamConfig(): RedteamConfig {
  return {
    description: "Demo Flow Configuration",
    prompts: ["{{prompt}}"],
    target: {
      id: "http",
      config: {},
    },
    plugins: [],
    strategies: [],
    numTests: REDTEAM_DEFAULTS.NUM_TESTS,
    maxConcurrency: REDTEAM_DEFAULTS.MAX_CONCURRENCY,
    frameworks: [],
  };
}

/**
 * Updates the target in a config based on agent type and provided data
 */
export function updateConfigTarget(
  config: RedteamConfig,
  agentType: AgentType,
  data: {
    // For "own" agent
    url?: string;
    method?: HttpMethod;
    headers?: HttpHeader[];
    body?: string;
    // For "our-ai" agent
    modelId?: FoundationModelId;
    modelLabel?: string;
    advancedConfig?: {
      temperature?: number;
      max_tokens?: number;
      top_p?: number;
      apiKey?: string;
    };
    providerConfigId?: string;
    managedSecretId?: string;
  }
): RedteamConfig {
  let newTarget: ProviderOptions;
  const updatedConfig: RedteamConfig = {
    ...config,
  };

  if (agentType === "own") {
    if (!data.url || !data.method || !data.body) {
      throw new Error("Missing required fields for own agent configuration");
    }
    newTarget = createHttpProviderOptions(data.url, data.method, data.headers || [], data.body);
    // Clear provider credential references for "own" agent flow
    delete updatedConfig.providerConfigId;
    delete updatedConfig.managedSecretId;
  } else {
    if (!data.modelId) {
      throw new Error("Missing model ID for our AI configuration");
    }
    newTarget = createFoundationModelProviderOptions(
      data.modelId,
      data.modelLabel,
      data.advancedConfig
    );
    // Set provider credential references if provided
    if (data.providerConfigId) {
      updatedConfig.providerConfigId = data.providerConfigId;
      delete updatedConfig.managedSecretId;
    } else if (data.managedSecretId) {
      updatedConfig.managedSecretId = data.managedSecretId;
      delete updatedConfig.providerConfigId;
    }
  }

  updatedConfig.target = newTarget;
  return updatedConfig;
}

/**
 * Updates prompts in a config
 */
export function updateConfigPrompts(config: RedteamConfig, prompts: string[]): RedteamConfig {
  return {
    ...config,
    prompts,
  };
}

/**
 * Updates plugins in a config
 * Converts plugin IDs and configurations to RedteamPlugin format
 */
export function updateConfigPlugins(
  config: RedteamConfig,
  pluginIds: string[],
  pluginConfigurations: Record<string, Record<string, unknown>>,
  frameworkId?: string
): RedteamConfig {
  const plugins: RedteamPlugin[] = [];

  pluginIds.forEach((pluginId) => {
    const pluginConfig = pluginConfigurations[pluginId];

    // Special handling for policy plugin - each policy becomes a separate plugin entry
    if (pluginId === "policy" && pluginConfig && pluginConfig.policies) {
      const policies = Array.isArray(pluginConfig.policies) ? pluginConfig.policies : [];
      policies.forEach((policy: any) => {
        if (policy && typeof policy === "object" && policy.text && policy.text.trim()) {
          // Create a separate plugin entry for each policy
          const policyConfig: PluginConfig = {
            policy: {
              id: policy.id || `policy-${Date.now()}-${Math.random()}`,
              text: policy.text.trim(),
              ...(policy.name && policy.name.trim() ? { name: policy.name.trim() } : {}),
            },
          };

          // Include graderGuidance if present
          if (pluginConfig.graderGuidance) {
            policyConfig.graderGuidance = pluginConfig.graderGuidance;
          }

          plugins.push({
            id: "policy",
            config: policyConfig,
          });
        }
      });
    } else if (pluginConfig && Object.keys(pluginConfig).length > 0) {
      // Plugin has configuration, return as object
      // Filter out empty string values and trim strings
      const cleanedConfig: PluginConfig = {};
      for (const key in pluginConfig) {
        const value = pluginConfig[key];
        if (Array.isArray(value)) {
          const cleanedArray = value.filter(
            (item) => typeof item === "string" && item.trim() !== ""
          );
          if (cleanedArray.length > 0) {
            cleanedConfig[key] = cleanedArray;
          }
        } else if (typeof value === "string" && value.trim() !== "") {
          cleanedConfig[key] = value.trim();
        } else if (typeof value === "number" || typeof value === "boolean") {
          cleanedConfig[key] = value;
        }
      }
      if (Object.keys(cleanedConfig).length > 0) {
        plugins.push({
          id: pluginId,
          config: cleanedConfig,
        });
      } else {
        // No valid config, add as string
        plugins.push(pluginId);
      }
    } else {
      // No configuration, return as string
      plugins.push(pluginId);
    }
  });

  return {
    ...config,
    plugins,
    ...(frameworkId ? { frameworks: [frameworkId] } : {}),
  };
}

/**
 * Updates strategies in a config
 * Converts strategy IDs to RedteamStrategy format
 */
export function updateConfigStrategies(
  config: RedteamConfig,
  strategyIds: string[]
): RedteamConfig {
  const strategies: RedteamStrategy[] = strategyIds.map((strategyId) => {
    // For now, strategies are just string IDs
    // In the future, if strategies need configuration, we can extend this
    return strategyId;
  });

  return {
    ...config,
    strategies,
  };
}

/**
 * Prepare config for API submission by converting to promptfoo format.
 *
 * The frontend RedteamConfig uses a different structure than promptfoo expects:
 * - Frontend: target (singular), plugins/strategies at root, numTests at root
 * - Promptfoo: targets (array), plugins/strategies under redteam, numTests under redteam
 *
 * This function converts the config to promptfoo format and extracts credential references.
 */
export function prepareConfigForSubmission(config: RedteamConfig): {
  configContent: Record<string, unknown>;
  providerConfigId?: string;
  managedSecretId?: string;
} {
  // Extract credential references before conversion
  const { providerConfigId, managedSecretId } = config;

  // Convert to promptfoo format using the same logic as YAML generation
  const unifiedConfig = convertToUnifiedConfig(config);

  return {
    configContent: unifiedConfig as unknown as Record<string, unknown>,
    providerConfigId,
    managedSecretId,
  };
}
